import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  User,
} from 'firebase/auth';
import { auth } from '../api/firebaseConfig';
import { ROLES } from '../constants';
import { useNavigate } from 'react-router-dom';
import {
  setAnalyticsUserId,
  setAnalyticsUserProperties,
  trackEvent,
} from '../api/analytics';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setUser(null);
        // Clear user ID in analytics when logged out
        setAnalyticsUserId(null);
      }

      const handleAuth = async () => {
        let isAdmin = false;
        if (user) {
          try {
            await user.getIdToken(true);
            const tokenResult = await user.getIdTokenResult();
            isAdmin = tokenResult?.claims?.admin === true;
            setUser({ ...user, role: (isAdmin && ROLES.admin) || '' });

            // Set user ID and properties in analytics
            setAnalyticsUserId(user.uid);
            setAnalyticsUserProperties({
              email_domain: user.email ? user.email.split('@')[1] : '',
              is_admin: isAdmin,
              provider:
                user.providerData.length > 0
                  ? user.providerData[0].providerId
                  : 'unknown',
              created_at: user.metadata.creationTime,
            });

            // Track user login event
            trackEvent('user_login', {
              method:
                user.providerData.length > 0
                  ? user.providerData[0].providerId
                  : 'unknown',
            });
          } catch (error) {
            console.error('Error getting token result:', error);
          }
        }
        setLoading(false);
      };

      handleAuth();
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // Track sign-in attempt with Google
      trackEvent('sign_in_attempt', { method: 'google', success: true });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      // Track failed sign-in attempt
      trackEvent('sign_in_attempt', {
        method: 'google',
        success: false,
        error: error.message,
      });
      throw new Error(
        'Failed to sign in with Google. Please check your configuration.',
      );
    } finally {
      setLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Track successful email sign-in
      trackEvent('sign_in_attempt', { method: 'email', success: true });
    } catch (error) {
      console.error('Error signing in with email:', error);
      // Track failed email sign-in
      trackEvent('sign_in_attempt', {
        method: 'email',
        success: false,
        error: error.message,
      });
      throw new Error(
        'Failed to sign in with email. Please check your credentials.',
      );
    } finally {
      setLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // Track successful signup
      trackEvent('sign_up', { method: 'email', success: true });
    } catch (error) {
      console.error('Error signing up with email:', error);
      // Track failed signup
      trackEvent('sign_up', {
        method: 'email',
        success: false,
        error: error.message,
      });
      throw new Error(
        'Failed to create account. The email might already be in use.',
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      // Track logout before actually logging out
      trackEvent('user_logout');
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw new Error('Failed to sign out. Please try again.');
    } finally {
      navigate('/');
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
